import React from 'react'
import cx from 'classnames'
import * as R from 'ramda'

import { Icon } from 'components/shared/Icon/Icon'
import { LockMessage } from 'components/shared/LockMessage/LockMessage'
import { Tooltip } from './common/Tooltip/Tooltip'

import { useButton } from './hooks'
import { ICON_SIZES, LOCK_ICON_SIZES, type ButtonSize } from './model'

import { LockIcon } from 'components/shared/Icon/model'

import styles from './Button.module.scss'

type ButtonProps = {
  ariaLabel?: string
  className?: string
  color?: string
  icon?: React.ComponentProps<typeof Icon>['icon']
  isContentReversed?: boolean
  isDisabled?: boolean
  isFullWidth?: boolean
  isLocked?: boolean
  label?: React.ReactNode
  message?: React.ReactNode
  onClick?: React.MouseEventHandler<HTMLButtonElement>
  size?: ButtonSize
  title?: string
  type?: React.ButtonHTMLAttributes<HTMLButtonElement>['type']
  variant?: string
}

export const Button = ({
  ariaLabel,
  className,
  color,
  icon,
  label,
  message,
  size,
  title,
  type = 'button',
  variant,
  onClick,
  isContentReversed = false,
  isDisabled,
  isFullWidth,
  isLocked,
}: ButtonProps) => {
  const { state, handlers } = useButton()

  const lockIconSize = size ? LOCK_ICON_SIZES[size] : undefined
  const lockOffset =
    lockIconSize === undefined ? undefined : `calc(50% - ${lockIconSize / 2}px)`

  const {
    arrowRef,
    arrowStyle,
    floating,
    floatingStyle,
    owner,
    reference,
    hasArrow,
    isToggled,
  } = state

  const {
    handleBlur,
    handleFocus,
    handleMouseEnter,
    handleMouseLeave,
    handleTouchEnd,
    handleTouchStart,
  } = handlers

  return (
    <>
      <button
        aria-label={ariaLabel}
        title={title}
        className={cx(
          styles.button,
          { [styles.fullWidth]: isFullWidth },
          size && [styles.size, styles[size]],
          variant && [styles.variant, styles[variant]],
          color && [styles.color, styles[color]],
          className,
        )}
        type={type}
        onClick={onClick}
        disabled={isLocked || isDisabled}
        {...(R.or(isLocked, Boolean(message)) && {
          ref: reference,
          onBlur: handleBlur,
          onFocus: handleFocus,
          onPointerEnter: handleMouseEnter,
          onPointerLeave: handleMouseLeave,
          onTouchStart: handleTouchStart,
          onTouchEnd: handleTouchEnd,
        })}
      >
        <span
          className={cx(styles.content, {
            [styles.reversed]: isContentReversed,
          })}
        >
          {icon ? (
            <span
              className={cx(styles.assets, {
                [styles.nospacing]: !Boolean(label),
              })}
            >
              <Icon
                className={cx(styles.icon, {
                  [styles.locked]: isLocked,
                })}
                icon={icon}
                size={size ? ICON_SIZES[size] : undefined}
              />
              {isLocked ? (
                <Icon
                  className={styles.lock}
                  icon={LockIcon}
                  size={lockIconSize}
                  style={{
                    top: lockOffset,
                    left: isContentReversed ? 'initial' : lockOffset,
                    right: isContentReversed ? lockOffset : 'initial',
                  }}
                />
              ) : null}
            </span>
          ) : null}
          {label ? (
            <span
              className={cx(styles.label, {
                [styles.nospacing]: !Boolean(icon),
              })}
            >
              {label}
            </span>
          ) : null}
        </span>
      </button>
      {Boolean(message) ? (
        <Tooltip
          {...{
            arrowRef,
            arrowStyle,
            floating,
            floatingStyle,
            message,
            hasArrow,
            isDisabled,
            isToggled,
          }}
        />
      ) : null}
      {isLocked ? (
        <LockMessage
          {...{
            arrowRef,
            arrowStyle,
            floating,
            floatingStyle,
            owner,
            hasArrow,
            isToggled,
          }}
        />
      ) : null}
    </>
  )
}
